
import { useState, useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Typography,
  ShimmerEffect,
  LogoDark,
} from "@/design-system/Atoms";
import { AppErrorState, AppEmptyState } from "@/design-system/Feedbacks";
import { useSelfScheduleService } from "@/rtkQuery/features/hooks/useSelfScheduleService";
import type { TimeSlot as ApiTimeSlot } from "@/rtkQuery/features/models/selfSchedule.model";
import { ROUTES } from "@/routes/routes.config";

interface TimeSlot {
  id: string;
  time: string;
  startDateTime: string;
  endDateTime: string;
  available: boolean;
}

interface TimePeriod {
  label: string;
  range: string;
  icon: string;
  slots: TimeSlot[];
}

const SelfScheduleTemplate = () => {
  const navigate = useNavigate();
  const PROVIDER_ID = 16;

  const {
    fetchProviderAvailability,
    providerAvailabilityLazyData,
    providerAvailabilityLazyIsLoading,
    providerAvailabilityLazyIsError,
    providerAvailabilityLazyErrorMessage,
    scheduleAppointment,
    scheduleAppointmentIsLoading,
    scheduleAppointmentIsError,
  } = useSelfScheduleService();

  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [error, setError] = useState<string>("");

  
  useEffect(() => {
    fetchProviderAvailability({ providerid: PROVIDER_ID });
  }, [fetchProviderAvailability]);

  
  const formatTime = (dateTimeString: string): string => {
    const date = new Date(dateTimeString);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, "0");
    return `${displayHours}:${displayMinutes} ${ampm}`;
  };

  
  const getDateFromDateTime = (dateTimeString: string): string => {
    return dateTimeString.split("T")[0];
  };

  
  const timeSlotsByDate = useMemo(() => {
    if (!providerAvailabilityLazyData?.Object) return {};

    const grouped: Record<string, ApiTimeSlot[]> = {};
    providerAvailabilityLazyData.Object.forEach((slot) => {
      const date = getDateFromDateTime(slot.StartDateTime);
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(slot);
    });

    
    const sortedDates = Object.keys(grouped).sort();
    const sortedGrouped: Record<string, ApiTimeSlot[]> = {};
    sortedDates.forEach((date) => {
      sortedGrouped[date] = grouped[date];
    });

    return sortedGrouped;
  }, [providerAvailabilityLazyData]);

  
  const availableDates = useMemo(() => {
    return Object.keys(timeSlotsByDate).sort();
  }, [timeSlotsByDate]);

  
  const firstAvailableDate = useMemo(() => {
    return availableDates.length > 0 ? availableDates[0] : null;
  }, [availableDates]);

  
  const formatDateForDisplay = (dateString: string): string => {
    const date = new Date(dateString + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  
  useEffect(() => {
    if (firstAvailableDate && !selectedDate) {
      setSelectedDate(firstAvailableDate);
    }
  }, [firstAvailableDate, selectedDate]);

  
  const groupSlotsByPeriod = (slots: ApiTimeSlot[]): TimePeriod[] => {
    const morning: TimeSlot[] = [];
    const afternoon: TimeSlot[] = [];
    const evening: TimeSlot[] = [];

    slots.forEach((slot, index) => {
      const time = formatTime(slot.StartDateTime);
      const hour = new Date(slot.StartDateTime).getHours();
      const timeSlot: TimeSlot = {
        id: `slot-${index}`,
        time,
        startDateTime: slot.StartDateTime,
        endDateTime: slot.EndDateTime,
        available: true,
      };

      if (hour >= 6 && hour < 12) {
        morning.push(timeSlot);
      } else if (hour >= 12 && hour < 17) {
        afternoon.push(timeSlot);
      } else if (hour >= 17 && hour < 22) {
        evening.push(timeSlot);
      }
    });

    const periods: TimePeriod[] = [];
    if (morning.length > 0) {
      periods.push({
        label: "Morning",
        range: "6 AM - 12 PM",
        icon: "bi-sunrise",
        slots: morning,
      });
    }
    if (afternoon.length > 0) {
      periods.push({
        label: "Afternoon",
        range: "12 PM - 5 PM",
        icon: "bi-sun",
        slots: afternoon,
      });
    }
    if (evening.length > 0) {
      periods.push({
        label: "Evening",
        range: "5 PM - 10 PM",
        icon: "bi-moon",
        slots: evening,
      });
    }

    return periods;
  };

  
  const timePeriods = useMemo(() => {
    if (!selectedDate || !timeSlotsByDate[selectedDate]) {
      return [];
    }
    return groupSlotsByPeriod(timeSlotsByDate[selectedDate]);
  }, [selectedDate, timeSlotsByDate]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<{
    date: string;
  }>({
    defaultValues: {
      date: "",
    },
  });

  const dateValue = watch("date");

  
  useEffect(() => {
    if (firstAvailableDate && !dateValue) {
      setValue("date", firstAvailableDate);
    }
  }, [firstAvailableDate, dateValue, setValue]);

  
  useEffect(() => {
    if (dateValue) {
      
      if (availableDates.includes(dateValue)) {
        setSelectedDate(dateValue);
        setSelectedTimeSlot(null); 
        setError("");
      } else {
        setError("Please select a date with available time slots.");
        setSelectedDate("");
        setSelectedTimeSlot(null);
      }
    }
  }, [dateValue, availableDates]);

  const handleTimeSlotClick = (slot: TimeSlot) => {
    setSelectedTimeSlot(selectedTimeSlot?.id === slot.id ? null : slot);
    setError("");
  };

  const onSubmit = async (data: { date: string }) => {
    if (!data.date) {
      setError("Please select a date.");
      return;
    }

    if (!selectedTimeSlot) {
      setError("Please select an available time slot.");
      return;
    }

    setError("");

    try {
      const response = await scheduleAppointment({
        startTime: selectedTimeSlot.startDateTime,
        providerid: PROVIDER_ID,
      }); 
      if (response && response.Result === "Success") {
        navigate(ROUTES.PRODUCTS_YOUR_OPTIONS);
      } else {
        const errorMessage =
          response?.Message ||
          "Failed to schedule appointment. Please ensure your patient record exists.";
        setError(errorMessage);
      }
    } catch (err: any) {      
      const errorMessage =
        err?.data?.message ||
        err?.error ||
        err?.Message ||
        "Failed to schedule appointment. Please ensure your patient record exists.";
      setError(errorMessage);
    }
  };
  
  if (providerAvailabilityLazyIsError) {
    return (
      <div className="relative bg-[var(--color-bg-cream)] bg-[url('/images/bg-wizard.svg')] bg-no-repeat bg-cover bg-top overflow-hidden min-h-screen">
        <div className="pb-[20rem]">
          <div className="container mx-auto px-4">
            <div className="flex flex-col lg:flex-row">
              <div className="lg:w-8/12 w-full mx-auto p-6 rounded-[20px]">
                <AppErrorState
                  message={providerAvailabilityLazyErrorMessage || "Failed to load provider availability."}
                  onRetry={() => fetchProviderAvailability({ providerid: PROVIDER_ID })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (
    !providerAvailabilityLazyIsLoading &&
    providerAvailabilityLazyData &&
    (!providerAvailabilityLazyData.Object || providerAvailabilityLazyData.Object.length === 0)
  ) {
    return (
      <div className="relative bg-[var(--color-bg-cream)] bg-[url('/images/bg-wizard.svg')] bg-no-repeat bg-cover bg-top overflow-hidden min-h-screen">
        <div className="pb-[20rem]">
          <div className="container mx-auto px-4">
            <div className="flex flex-col lg:flex-row">
              <div className="lg:w-8/12 w-full mx-auto p-6 rounded-[20px]">
                <AppEmptyState message="No provider availability found." />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-[var(--color-bg-cream)] bg-[url('/images/bg-wizard.svg')] bg-no-repeat bg-cover bg-top overflow-hidden min-h-screen">
      <div className="pb-[20rem]">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row">
            <div className="lg:w-8/12 w-full mx-auto p-6 rounded-[20px]">
              {providerAvailabilityLazyIsLoading ? (
                <div className="w-full p-6 rounded-2xl mt-10 bg-white/70 shadow-[0_4px_20px_rgba(0,0,0,0.08)] backdrop-blur-md">
                  <ShimmerEffect className="h-[80px] w-full mt-10 mb-6" />
                  <ShimmerEffect className="h-[38px] w-[140px] my-6" />
                  <ShimmerEffect className="h-[108px] w-full" />
                </div>
              ) : (
                <div className="w-full p-6 rounded-2xl mt-10 bg-white/70 shadow-[0_4px_20px_rgba(0,0,0,0.08)] backdrop-blur-md">
                  <LogoDark className="mb-10" />
                  <Typography
                    variant="sectionHeading"  
                    className="mb-6"
                  >
                    Schedule Your Appointment
                  </Typography>
                  <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <div className="space-y-6">
                      <div>
                        <Typography
                          variant="sectionDescriptionSmall"
                          as="h4"
                          className="mb-5 text-[var(--color-gray-900)]"
                        >
                          Appointment Date <span className="text-red-500">*</span>
                        </Typography>
                        
                        <Controller
                          name="date"
                          control={control}
                          rules={{
                            required: "Please select a date",
                            validate: (value) => {
                              if (!value) return "Please select a date";
                              if (!availableDates.includes(value)) {
                                return "Please select a date with available time slots.";
                              }
                              return true;
                            },
                          }}
                          render={({ field }) => (
                            <>
                              <select
                                id="date"
                                {...field}
                                value={field.value || selectedDate || ""}
                                onChange={(e) => {
                                  field.onChange(e);
                                  setError("");
                                }}
                                className="w-full p-4 border border-gray-300 rounded-md bg-white text-base text-gray-900 focus:border-[#8c8d90] focus:outline-none focus:ring-4 focus:ring-[rgba(24,26,32,0.25)] appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22currentColor%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Cpolyline points=%226 9 12 15 18 9%22%3E%3C/polyline%3E%3C/svg%3E')] bg-no-repeat bg-[length:20px] bg-[right_12px_center] pr-10"
                              >
                                <option value="" disabled>
                                  Select a date
                                </option>
                                {availableDates.map((date) => (
                                  <option key={date} value={date}>
                                    {formatDateForDisplay(date)}
                                  </option>
                                ))}
                              </select>
                              {errors.date && (
                                <p className="text-red-600 text-sm mt-1">
                                  {errors.date.message}
                                </p>
                              )}
                            </>
                          )}
                        />
                      </div>

                      <div>
                        <Typography
                          variant="sectionDescriptionSmall"
                          as="h4"
                          className="mb-5 text-[var(--color-gray-900)]"
                        >
                          Available Times <span className="text-red-500">*</span>
                        </Typography>

                        {selectedDate && timePeriods.length === 0 ? (
                          <div className="p-4 border border-gray-300 bg-gray-50 rounded-md text-sm text-gray-700">
                            No time slots available.
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {timePeriods.map((period) => (
                              <div
                                key={period.label}
                                className="bg-white rounded-lg p-4 shadow-sm border border-gray-200"
                              >                               
                                <div className="flex items-center gap-2 mb-4">
                                  <i className={`${period.icon} text-xl`} aria-hidden="true"></i>
                                  <Typography
                                    variant="sectionDescriptionSmall"
                                    className="text-lg font-semibold"
                                  >
                                    {period.label} ({period.range})
                                  </Typography>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {period.slots
                                    .filter((slot) => slot.available)
                                    .map((slot) => (
                                      <button
                                        key={slot.id}
                                        type="button"
                                        onClick={() => handleTimeSlotClick(slot)}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                          selectedTimeSlot?.id === slot.id
                                            ? "bg-[var(--color-green-100)] border-2 border-[var(--color-green-200)] text-gray-900"
                                            : "bg-gray-100 border border-gray-300 text-gray-700 hover:bg-gray-200"
                                        }`}
                                      >
                                        {slot.time}
                                      </button>
                                    ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {error && (
                        <div className="p-4 border border-red-300 bg-red-50 rounded-md text-sm text-red-700">
                          {error}
                        </div>
                      )}
                      <div className="flex justify-end mt-8">
                        <Button
                          variant="secondary"
                          size="xl"
                          type="submit"
                          disabled={scheduleAppointmentIsLoading || !selectedTimeSlot}
                          loading={scheduleAppointmentIsLoading}
                          className="!w-full md:!w-auto"
                        >
                          Schedule Appointment
                        </Button>
                      </div>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelfScheduleTemplate;
